/**
 * Iter 80 — Plan Export Modal.
 *
 * Mounts in the Sheet Viewer toolbar. Lets the user:
 *   - choose a template (10 presets)
 *   - select which sheets to include (current sheet by default)
 *   - toggle overlay inclusion (markups / measurements / objects / pipes)
 *   - toggle confidential stamp / signature block / QR / watermark / cover sheet
 *
 * Submits to POST /api/projects/{id}/plan-exports → polls GET /plan-exports/{id}
 * → auto-downloads the finished PDF.
 *
 * Visual DNA: inherits the same dark/orange tactical aesthetic as ShareModal.
 * Every interactive element ≥ 44 px (iter 79 cert).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Download, X, FileDown, CheckCircle2, Loader2, AlertCircle, ScanLine } from "lucide-react";
import { api } from "@/lib/api";

const Toggle = ({ on, onClick, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`min-h-[44px] flex items-center justify-between gap-3 w-full px-4 rounded-md text-sm transition-colors ${
      on
        ? "bg-[var(--fop-primary)]/20 border border-[var(--fop-primary)]/60 text-[var(--fop-text)]"
        : "bg-[var(--fop-surface-2)] border border-[var(--fop-border-2)] text-[var(--fop-text-2)] hover:text-[var(--fop-text)]"
    }`}
  >
    <span className="text-[13px] font-medium">{label}</span>
    <span className={`h-5 w-9 rounded-full relative transition-colors ${on ? "bg-[var(--fop-primary)]" : "bg-[var(--fop-border-2)]"}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${on ? "left-4" : "left-0.5"}`} />
    </span>
  </button>
);

export default function PlanExportModal({ open, onClose, projectId, sheets, currentSheetId }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("field");
  const [sheetIds, setSheetIds] = useState([]);
  const [opts, setOpts] = useState({
    include_markups: true,
    include_measurements: true,
    include_objects: true,
    include_pipes: true,
    show_confidential_stamp: false,
    show_signature_block: false,
    show_qr_code: true,
    cover_sheet: true,
  });
  const [title, setTitle] = useState("");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Load template presets when modal opens
  useEffect(() => {
    if (!open) return;
    setErr(null); setJob(null);
    setSheetIds(currentSheetId ? [currentSheetId] : []);
    api.get("/plan-exports/templates")
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => setTemplates([]));
  }, [open, currentSheetId]);

  // Apply template defaults whenever the user picks a different template
  const activeTemplate = useMemo(
    () => templates.find((t) => t.key === selectedTemplate),
    [templates, selectedTemplate]
  );
  useEffect(() => {
    if (!activeTemplate) return;
    const d = activeTemplate.defaults || {};
    setOpts((o) => ({
      ...o,
      include_markups: d.include_markups ?? o.include_markups,
      include_measurements: d.include_measurements ?? o.include_measurements,
      include_objects: d.include_objects ?? o.include_objects,
      include_pipes: d.include_pipes ?? o.include_pipes,
      show_confidential_stamp: d.show_confidential_stamp ?? false,
      show_signature_block: d.show_signature_block ?? false,
      show_qr_code: d.show_qr_code ?? true,
    }));
  }, [activeTemplate]);

  // Poll the job until completion
  useEffect(() => {
    if (!job || job.status === "completed" || job.status === "failed") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/plan-exports/${job.id}`);
        setJob(r.data);
        if (r.data?.status === "completed" || r.data?.status === "failed") clearInterval(t);
      } catch {
        clearInterval(t);
      }
    }, 800);
    return () => clearInterval(t);
  }, [job]);

  if (!open) return null;

  const toggleSheet = (id) =>
    setSheetIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const generate = async () => {
    if (!sheetIds.length) { setErr("Select at least one sheet."); return; }
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/projects/${projectId}/plan-exports`, {
        sheet_ids: sheetIds,
        template: selectedTemplate,
        title: title || undefined,
        ...opts,
      });
      setJob(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!job?.download_url) return;
    // Use the api client (already authenticated) — request as blob.
    try {
      const r = await api.get(`/plan-exports/${job.id}/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title || activeTemplate?.label || "plan-export"}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr("Download failed");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-6"
      data-testid="plan-export-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-xl sm:rounded-xl border border-[var(--fop-border-2)] bg-[var(--fop-surface)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--fop-border-2)]">
          <div className="flex items-center gap-2">
            <FileDown size={18} className="text-[var(--fop-primary)]" />
            <h2 className="text-base font-semibold text-[var(--fop-text)]">Export Plan Package</h2>
          </div>
          <button
            onClick={onClose}
            className="h-11 w-11 flex items-center justify-center rounded-md text-[var(--fop-text-2)] hover:bg-[var(--fop-surface-2)]"
            data-testid="plan-export-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Template picker */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--fop-text-2)] mb-2">Package Template</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {templates.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setSelectedTemplate(t.key)}
                  data-testid={`plan-export-template-${t.key}`}
                  className={`min-h-[44px] text-left px-3 py-2 rounded-md border text-[12px] transition-colors ${
                    selectedTemplate === t.key
                      ? "border-[var(--fop-primary)] bg-[var(--fop-primary)]/15 text-[var(--fop-text)]"
                      : "border-[var(--fop-border-2)] bg-[var(--fop-surface-2)] text-[var(--fop-text-2)] hover:text-[var(--fop-text)]"
                  }`}
                >
                  <div className="font-semibold">{t.label}</div>
                  <div className="text-[10px] text-[var(--fop-text-2)] line-clamp-2 mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--fop-text-2)] mb-2">Package Title (optional)</div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={activeTemplate?.label || "Plan Export"}
              maxLength={120}
              data-testid="plan-export-title"
              className="w-full min-h-[44px] px-3 rounded-md bg-[var(--fop-surface-2)] border border-[var(--fop-border-2)] text-sm text-[var(--fop-text)] placeholder:text-[var(--fop-text-2)] focus:outline-none focus:border-[var(--fop-primary)]/60"
            />
          </div>

          {/* Sheet selector */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--fop-text-2)] mb-2 flex items-center justify-between">
              <span>Sheets ({sheetIds.length}/{(sheets || []).length})</span>
              <button
                className="text-[var(--fop-primary)] text-[11px] uppercase tracking-wider hover:underline"
                onClick={() => setSheetIds((sheets || []).map((s) => s.id))}
                data-testid="plan-export-select-all"
              >Select all</button>
            </div>
            <div className="max-h-44 overflow-y-auto rounded-md border border-[var(--fop-border-2)] divide-y divide-[var(--fop-border-2)]">
              {(sheets || []).map((s) => (
                <label
                  key={s.id}
                  className="min-h-[44px] flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[var(--fop-surface-2)]"
                >
                  <input
                    type="checkbox"
                    checked={sheetIds.includes(s.id)}
                    onChange={() => toggleSheet(s.id)}
                    data-testid={`plan-export-sheet-${s.id}`}
                    className="accent-[var(--fop-primary)] h-5 w-5"
                  />
                  <span className="font-mono text-[12px] text-[var(--fop-text)]">{s.sheet_number || "—"}</span>
                  <span className="text-[12px] text-[var(--fop-text-2)] truncate">{s.name || s.title || ""}</span>
                  <span className="ml-auto text-[10px] text-[var(--fop-text-2)] font-mono">REV {s.revision_number ?? "0"}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Overlay toggles */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--fop-text-2)] mb-2">Overlays</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Toggle on={opts.include_markups} onClick={() => setOpts((o) => ({ ...o, include_markups: !o.include_markups }))} label="Markups" testid="plan-export-inc-markups" />
              <Toggle on={opts.include_measurements} onClick={() => setOpts((o) => ({ ...o, include_measurements: !o.include_measurements }))} label="Measurements" testid="plan-export-inc-measurements" />
              <Toggle on={opts.include_objects} onClick={() => setOpts((o) => ({ ...o, include_objects: !o.include_objects }))} label="Objects + Conflicts" testid="plan-export-inc-objects" />
              <Toggle on={opts.include_pipes} onClick={() => setOpts((o) => ({ ...o, include_pipes: !o.include_pipes }))} label="Utility Runs" testid="plan-export-inc-pipes" />
            </div>
          </div>

          {/* Stamps */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--fop-text-2)] mb-2">Stamps</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Toggle on={opts.cover_sheet} onClick={() => setOpts((o) => ({ ...o, cover_sheet: !o.cover_sheet }))} label="Cover Sheet" testid="plan-export-cover" />
              <Toggle on={opts.show_qr_code} onClick={() => setOpts((o) => ({ ...o, show_qr_code: !o.show_qr_code }))} label="QR Code" testid="plan-export-qr" />
              <Toggle on={opts.show_confidential_stamp} onClick={() => setOpts((o) => ({ ...o, show_confidential_stamp: !o.show_confidential_stamp }))} label="Confidential Stamp" testid="plan-export-confidential" />
              <Toggle on={opts.show_signature_block} onClick={() => setOpts((o) => ({ ...o, show_signature_block: !o.show_signature_block }))} label="Signature Block" testid="plan-export-signature" />
            </div>
          </div>

          {err && (
            <div className="flex items-start gap-2 text-[12px] text-[var(--fop-danger,#ef4444)] bg-[var(--fop-danger,#ef4444)]/10 border border-[var(--fop-danger,#ef4444)]/40 rounded-md px-3 py-2" data-testid="plan-export-error">
              <AlertCircle size={14} className="mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          {/* Job status */}
          {job && (
            <div className="border border-[var(--fop-border-2)] rounded-md p-3 bg-[var(--fop-surface-2)]" data-testid="plan-export-job-status">
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-[var(--fop-text-2)]">Job <span className="font-mono text-[var(--fop-text)]">{job.id}</span></span>
                <span className={`uppercase tracking-wider text-[11px] font-semibold ${
                  job.status === "completed" ? "text-[#22c55e]" :
                  job.status === "failed" ? "text-[#ef4444]" : "text-[var(--fop-primary)]"
                }`}>{job.status}</span>
              </div>
              {job.status === "completed" && (
                <div className="mt-2 flex items-center justify-between text-[11px] text-[var(--fop-text-2)]">
                  <span>{job.output_pages} pages · {(job.output_bytes / 1024).toFixed(0)} KB · {job.duration_ms} ms</span>
                  <button
                    onClick={download}
                    data-testid="plan-export-download"
                    className="min-h-[44px] inline-flex items-center gap-2 px-4 rounded-md bg-[var(--fop-cta-bg)] text-white font-medium text-sm hover:bg-[var(--fop-cta-bg-hover)]"
                  >
                    <Download size={14} /> Download PDF
                  </button>
                </div>
              )}
              {job.status === "failed" && (
                <div className="mt-2 text-[12px] text-[#ef4444]">{job.error || "Export failed."}</div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-[var(--fop-border-2)] flex items-center justify-between gap-2 sticky bottom-0 bg-[var(--fop-surface)]">
          <div className="text-[11px] text-[var(--fop-text-2)] flex items-center gap-1.5">
            <ScanLine size={12} /> Vector overlay · scale preserved
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="min-h-[44px] px-4 rounded-md border border-[var(--fop-border-2)] text-[var(--fop-text-2)] hover:text-[var(--fop-text)] text-sm"
              data-testid="plan-export-cancel"
            >Close</button>
            <button
              onClick={generate}
              disabled={busy || !sheetIds.length}
              className="min-h-[44px] px-5 rounded-md bg-[var(--fop-cta-bg)] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="plan-export-generate"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {busy ? "Generating…" : "Generate Package"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
